import React from 'react';
import {
  AddOperandButton,
  ConditionType,
  defaultOperator,
  defaultValue,
  Filter,
  filterOperatorOptions,
  getFilterOptionsWithLegacy,
  getOperator,
  operatorColors,
  Select,
} from '@fabric-ui/components';
import { Delete } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { renameProp } from '@fabric-ui/utils';
import { Box, IconButton, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { setNestedValue } from '@utils/utils';
import { getFieldTypeInfo } from '../../../utils/filterUtils';
import { ConditionOperand, FieldFilterValue, LogicalOperatorType } from '../types';
import FilterInput from './FilterInput';

interface FilterByConditionProps {
  initialFilter: ConditionOperand;
  conditionValue: FieldFilterValue;
  operator: LogicalOperatorType;
  isMultipleCondition: boolean;
  disablePortalInOperatorSelect: boolean;
  type: string;
  handleSelectConditionChange: (value: unknown, index: number) => void;
  setErrors: React.Dispatch<React.SetStateAction<ConditionOperand[]>>;
  setConditionValue: React.Dispatch<React.SetStateAction<FieldFilterValue>>;
  onChange: (field: string, value: unknown) => void;
  field: string;
  onFilterChange: ((filter: Filter) => void) | undefined;
  errors: ConditionOperand[];
}

export const FilterByCondition: React.FC<FilterByConditionProps> = ({
  initialFilter,
  conditionValue,
  operator,
  isMultipleCondition,
  disablePortalInOperatorSelect,
  type,
  handleSelectConditionChange,
  setErrors,
  setConditionValue,
  onChange,
  field,
  onFilterChange,
  errors,
}) => {
  const { isNumberField } = getFieldTypeInfo(type);

  return (
    <Box sx={{ gap: 1, p: 3, display: 'block', maxHeight: 380, overflowY: 'auto' }}>
      {initialFilter &&
        conditionValue[operator]!.map((operand: ConditionOperand, index: number, operands: ConditionOperand[]) => (
          /* eslint-disable-next-line react/no-array-index-key */
          <Box key={index}>
            <Box
              sx={{
                ...flex.itemsCenter,
                p: '12px',
                border: '1px solid var(--color-border-base-secondary)',
                gap: 1,
                borderRadius: '6px',
                // @ts-ignore
                '.MuiPaper-root': theme => ({ ...theme.components.MuiAutocomplete.styleOverrides.paper }),
              }}>
              <Select
                size="small"
                style={{ width: 170, input: { fontWeight: 600 } }}
                isRequired
                muiProps={{ disablePortal: disablePortalInOperatorSelect }}
                placeholder="Select Operator"
                value={getOperator(operand.operator)}
                onChange={value => handleSelectConditionChange(value, index)}
                options={getFilterOptionsWithLegacy(filterOperatorOptions[type], operand.operator!)}
              />
              <FilterInput
                value={operand.value}
                operator={operand.operator}
                numberField={isNumberField}
                inputWidth={210}
                onChange={value => {
                  setErrors([]);
                  setConditionValue((prev: FieldFilterValue) => setNestedValue(`${operator}.${index}.value`, prev, value));
                }}
                options={filterOperatorOptions[type]}
                error={errors[index]?.value}
              />
              <IconButton
                aria-label="Delete"
                disabled={operands.length === 1 && conditionValue[operator]![index].value === ''}
                onClick={() => {
                  if (operands.length === 1) {
                    setConditionValue((prev: FieldFilterValue) => setNestedValue(`${operator}.${index}.value`, prev, ''));
                    onChange(field, []);
                    onFilterChange?.({
                      expression: {
                        fieldName: field,
                        [ConditionType[type]]: { [defaultOperator[ConditionType[type]]]: defaultValue[ConditionType[type]]() },
                      },
                    });
                  } else {
                    setConditionValue((prev: FieldFilterValue) => ({
                      [operator]: prev[operator]!.filter((_o: ConditionOperand, i: number) => i !== index),
                    }));
                  }
                  setErrors([]);
                }}>
                <Delete />
              </IconButton>
            </Box>
            <Box sx={{ ...flex.center, py: 1 }}>
              {operands.length > 1 && index !== operands.length - 1 && (
                <ToggleButtonGroup
                  color="white"
                  value={operator}
                  onChange={() =>
                    setConditionValue((prev: FieldFilterValue) => renameProp(operator, operator === 'and' ? 'or' : 'and', prev))
                  }
                  size="xSmall"
                  exclusive>
                  <ToggleButton value="and">
                    <Box
                      sx={{
                        fontWeight: 600,
                        fontSize: 13,
                        color: operator === 'and' ? operatorColors.and : 'inherit',
                      }}>
                      AND
                    </Box>
                  </ToggleButton>
                  <ToggleButton value="or">
                    <Box
                      sx={{
                        fontWeight: 600,
                        fontSize: 13,
                        color: operator === 'or' ? operatorColors.or : 'inherit',
                      }}>
                      OR
                    </Box>
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            </Box>
          </Box>
        ))}
      {isMultipleCondition && (
        <Box sx={flex.center}>
          <AddOperandButton
            operator={operator}
            isAdd
            onClick={() => setConditionValue((prev: FieldFilterValue) => ({ [operator]: [...prev[operator]!, initialFilter] }))}
          />
        </Box>
      )}
    </Box>
  );
};
